// Inauguration gate.
//
// The public site asks GET /api/ceremony. A new browser session sees Launch
// website only when that reply is exactly enabled: true. A missing server,
// a timeout, or any other reply opens the normal site, so a down API cannot
// trap visitors on the gate. Crawlers skip the gate. The countdown still asks
// the Google Sheet to start at 1 once. No Apps Script secret is read or sent
// from here.

import { apiUrl, hasApi } from "./api";
import {
  alreadyCountedToday,
  beginVisitorCount,
  clearVisitorCountedToday,
  markVisitorCountedToday,
  notifyVisitorRefresh,
} from "./visitorCount";

export const CEREMONY_SEEN_KEY = "apmercl_ceremony_done";
export const LAUNCH_BUTTON_LABEL = "Launch website";
const FLAG_TIMEOUT_MS = 3500;
const COUNT_TIMEOUT_MS = 20000;

let countStartSent = false;

export function ceremonyAlreadySeen() {
  try {
    return window.sessionStorage.getItem(CEREMONY_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function markCeremonySeen() {
  try {
    window.sessionStorage.setItem(CEREMONY_SEEN_KEY, "1");
  } catch {
    /* private mode: a refresh may show the gate again */
  }
}

const CRAWLER = /bot|crawler|spider|preview|headless|slurp|facebookexternalhit/i;

/**
 * "gate" only for a new human session while the flag is strictly on.
 * Crawlers, a finished session, and anything other than enabled true open the site.
 */
export function ceremonyGateDecision(alreadySeen, enabled, userAgent) {
  if (alreadySeen) return "site";
  const ua = userAgent != null
    ? String(userAgent)
    : (typeof navigator !== "undefined" ? navigator.userAgent || "" : "");
  if (CRAWLER.test(ua)) return "site";
  if (enabled === true) return "gate";
  return "site";
}

/** True only for a clear enabled flag. Any failure, including no API, is false. */
export async function loadCeremonyEnabled(timeoutMs = FLAG_TIMEOUT_MS) {
  if (!hasApi()) return false;
  const url = apiUrl("/api/ceremony");
  if (!url) return false;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return false;
    const data = await response.json();
    return !!(data && data.enabled === true);
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}

async function postCeremonyLaunch() {
  if (!hasApi()) return null;
  const url = apiUrl("/api/ceremony/launch");
  if (!url) return null;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), COUNT_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "POST",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-APMERCL-Ceremony": "launch",
      },
      body: "{}",
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Start the sheet at 1 the first time the public countdown finishes.
 * Later calls do not reset it. A failure is swallowed so the ceremony still finishes.
 * This browser is marked as already counted today before the request, so the footer
 * does not add a second visitor on top of that 1. If the sheet was already launched,
 * the mark is removed and the normal daily count applies.
 */
export function requestFirstCountStart() {
  if (countStartSent) return Promise.resolve();
  countStartSent = true;
  const hadCounted = alreadyCountedToday();
  if (!hadCounted) markVisitorCountedToday();
  return (async () => {
    try {
      let data = await beginVisitorCount();
      if (!data || typeof data.started !== "boolean") {
        data = await postCeremonyLaunch();
      }
      if (data && data.started === false && !hadCounted) {
        clearVisitorCountedToday();
      }
    } catch {
      /* keep the day mark so a begin that landed is not counted twice */
    }
    notifyVisitorRefresh();
  })();
}
